/**
 * Every prop kind's art. Pure drawing in world space (tile units through
 * `iso`), lit by the shared key light. Props that never change are drawn once
 * into a sprite by the scene; the few that move (water, fire, screens) are
 * marked `LIVE` and drawn every frame.
 */
import type { HatId } from '../../../shared/cosmetics.ts'
import { doorOf, footprint, hash01, type Prop, type PropKind } from '../../../shared/world.ts'
import { drawHatAt } from './hats.ts'
import { buildingTop, drawBuilding, wallHeight, windowsOf } from './buildings.ts'
import { drawExtra } from './props-extra.ts'
import { drawClutter, drawFlatClutter } from './props-clutter.ts'
import { alpha, mix, shade } from './color.ts'
import { blobShadow, box, castShadow, cyl, iso, poly, rnd, TH, TW, ZH, type Pt } from './iso.ts'

type Ctx = CanvasRenderingContext2D

export interface Light { x: number; y: number; z: number; r: number; color: string; a: number; flicker?: number }

/** Kinds redrawn every frame. */
export const LIVE = new Set<PropKind>(['fountain', 'campfire', 'arcade', 'djbooth', 'pond', 'water', 'dancefloor'])

/** Height in world units, for sprite bounds. */
export const HEIGHT: Partial<Record<PropKind, number>> = {
  tree: 3.4, pine: 3.6, palm: 3.8, lamppost: 3.2, fountain: 2.2, parasol: 2.6, shelf: 2.4, arcade: 2.2, vending: 2.3,
  lamp: 2.2, signpost: 1.9, counter: 1.6, piano: 1.6, djbooth: 1.5, plant: 1.6, sandcastle: 1.2, campfire: 1.2,
  building: 5.2, hatstand: 2.2, willow: 2.9, sakura: 2.7, bamboo: 3.8, windmill: 6.5, tramstop: 2.5, gate: 3.3,
  stonelantern: 1.4, lanternpost: 2.6, streetlamp: 3.3, trafficlight: 2.6, statue: 1.8, gazebo: 2.8, boat: 1.2, screen: 1.8, rock: 0.9, hay: 0.8, crop: 1,
  crate: 0.8, barrel: 0.9, pot: 1.8, mailbox: 1.3, bin: 0.8, bicycle: 0.9, fence: 0.8, stall: 2.1, cart: 2.1, well: 2, birdbath: 1.1,
  bollard: 0.6, aboard: 1, festoon: 3.2, laundry: 2.6, reeds: 1.2,
}

/** How many looks a kind has; a prop's look is picked from its position, so sprites can be shared. */
const VARIANTS: Partial<Record<PropKind, number>> = { tree: 5, bush: 5, plant: 3, shelf: 4, flowers: 3, palm: 2, pine: 2, sakura: 5, willow: 3, bamboo: 4, rock: 3, crate: 2, pot: 4, reeds: 3 }

/** Animated kinds beyond the floor ones, plus the lighthouse. */
export function isLive(p: Prop): boolean {
  return LIVE.has(p.kind) || p.kind === 'windmill' || p.kind === 'laundry' || p.kind === 'birdbath' || p.kind === 'trafficlight' || (p.kind === 'gate' && p.variant === 'neon') || (p.kind === 'statue' && p.color === '#ffd166')
}

export function variantOf(p: Prop): number {
  return Math.floor(hash01(Math.floor(p.x), Math.floor(p.y), 7) * (VARIANTS[p.kind] ?? 1))
}

/** Props that look identical share one cached sprite. */
export function spriteKey(p: Prop): string {
  return [p.kind, p.w, p.d, p.rot, p.color, p.text, p.roof, p.door, p.face, variantOf(p)].join('|')
}

const INK = '#3a3552'
const WOOD = '#b98559'
const LEAF = ['#5cc98a', '#4fb97d', '#6ad89a', '#7ee0a8', '#45a870']

/** Draw a quad on the plane y = const (a face looking towards +y). */
function faceY(y: number, x0: number, x1: number, z0: number, z1: number): Pt[] {
  return [iso(x0, y, z0), iso(x1, y, z0), iso(x1, y, z1), iso(x0, y, z1)]
}
/** Draw a quad on the plane x = const (a face looking towards +x). */
function faceX(x: number, y0: number, y1: number, z0: number, z1: number): Pt[] {
  return [iso(x, y0, z0), iso(x, y1, z0), iso(x, y1, z1), iso(x, y0, z1)]
}

/** Run `draw` with tile space mapped onto the floor plane at height z: draw ellipses and rects in tiles. */
export function onFloor(ctx: Ctx, z: number, draw: () => void): void {
  ctx.save()
  ctx.translate(0, -z * ZH)
  ctx.transform(TW / 2, TH / 2, -TW / 2, TH / 2, 0, 0)
  draw()
  ctx.restore()
}

function sphere(ctx: Ctx, p: Pt, r: number, color: string): void {
  const g = ctx.createRadialGradient(p.x - r * 0.35, p.y - r * 0.4, r * 0.1, p.x, p.y, r)
  g.addColorStop(0, shade(color, 0.22))
  g.addColorStop(0.7, color)
  g.addColorStop(1, shade(color, -0.16))
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, Math.PI * 2); ctx.fill()
}

// ─── Flat props (floor layer) ─────────────────────────────────────────────

export function drawFlat(ctx: Ctx, p: Prop): void {
  const { w, d } = footprint(p)
  switch (p.kind) {
    case 'rug': {
      const c = p.color ?? '#e9a46b'
      onFloor(ctx, 0.004, () => {
        ctx.beginPath(); ctx.roundRect(p.x, p.y, w, d, 0.35); ctx.fillStyle = mix('#fbf3ea', c, 0.32); ctx.fill()
        ctx.beginPath(); ctx.roundRect(p.x + 0.25, p.y + 0.25, w - 0.5, d - 0.5, 0.22); ctx.fillStyle = mix('#fbf3ea', c, 0.16); ctx.fill()
        ctx.strokeStyle = alpha(c, 0.35); ctx.lineWidth = 0.06
        ctx.beginPath(); ctx.roundRect(p.x + 0.45, p.y + 0.45, w - 0.9, d - 0.9, 0.12); ctx.stroke()
      })
      return
    }
    case 'stone':
      onFloor(ctx, 0.01, () => {
        ctx.beginPath(); ctx.ellipse(p.x + 0.52, p.y + 0.55, 0.36, 0.3, 0.4, 0, Math.PI * 2); ctx.fillStyle = '#8f8a86'; ctx.fill()
        ctx.beginPath(); ctx.ellipse(p.x + 0.5, p.y + 0.5, 0.36, 0.3, 0.4, 0, Math.PI * 2); ctx.fillStyle = '#c9c3bb'; ctx.fill()
      })
      return
    case 'picnic': {
      const c = p.color ?? '#ff8fab'
      onFloor(ctx, 0.01, () => {
        ctx.fillStyle = '#ffffff'; ctx.fillRect(p.x + 0.1, p.y + 0.1, w - 0.2, d - 0.2)
        ctx.fillStyle = alpha(c, 0.75)
        const n = 6, s = (w - 0.2) / n
        for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) if ((i + j) % 2 === 0) ctx.fillRect(p.x + 0.1 + i * s, p.y + 0.1 + j * s, s, s)
        ctx.fillStyle = alpha(c, 0.35)
        for (let i = 0; i < n; i++) ctx.fillRect(p.x + 0.1 + i * s + s * 0.35, p.y + 0.1, s * 0.3, d - 0.2)
      })
      box(ctx, p.x + w - 0.62, p.y + 0.22, 0.01, 0.4, 0.3, 0.26, '#c98d52')
      return
    }
    case 'towel': {
      const c = p.color ?? '#6ec6ff'
      onFloor(ctx, 0.01, () => {
        ctx.beginPath(); ctx.roundRect(p.x + 0.12, p.y + 0.1, w - 0.24, d - 0.2, 0.08); ctx.fillStyle = c; ctx.fill()
        ctx.fillStyle = 'rgba(255,255,255,0.7)'
        for (let k = 0.35; k < d - 0.2; k += 0.45) ctx.fillRect(p.x + 0.12, p.y + k, w - 0.24, 0.12)
      })
      return
    }
    case 'pond':
      onFloor(ctx, 0, () => {
        const cx = p.x + w / 2, cy = p.y + d / 2
        ctx.beginPath(); ctx.ellipse(cx, cy, w / 2 - 0.02, d / 2 - 0.02, 0, 0, Math.PI * 2); ctx.fillStyle = '#9b8f7d'; ctx.fill()
        ctx.beginPath(); ctx.ellipse(cx, cy, w / 2 - 0.2, d / 2 - 0.2, 0, 0, Math.PI * 2); ctx.fillStyle = '#6fb7d8'; ctx.fill()
        ctx.beginPath(); ctx.ellipse(cx + 0.3, cy + 0.25, w / 2 - 0.7, d / 2 - 0.65, 0, 0, Math.PI * 2); ctx.fillStyle = '#4f9cc4'; ctx.fill()
        // Rim stones.
        for (let i = 0; i < 26; i++) {
          const a = (i / 26) * Math.PI * 2
          ctx.beginPath(); ctx.ellipse(cx + Math.cos(a) * (w / 2 - 0.1), cy + Math.sin(a) * (d / 2 - 0.1), 0.2, 0.15, a, 0, Math.PI * 2)
          ctx.fillStyle = i % 3 ? '#b7ad9f' : '#a39886'; ctx.fill()
        }
        for (const [lx, ly] of [[0.25, 0.3], [0.7, 0.62], [0.35, 0.72]] as const) {
          ctx.beginPath(); ctx.arc(p.x + w * lx, p.y + d * ly, 0.22, 0.4, Math.PI * 2 - 0.1); ctx.lineTo(p.x + w * lx, p.y + d * ly); ctx.fillStyle = '#5cbf7a'; ctx.fill()
        }
      })
      return
    case 'water':
      onFloor(ctx, 0, () => {
        const g = ctx.createLinearGradient(0, p.y, 0, p.y + d)
        g.addColorStop(0, '#3f8fd0'); g.addColorStop(0.7, '#5fb3e4'); g.addColorStop(1, '#8fd6ef')
        ctx.fillStyle = g; ctx.fillRect(p.x, p.y, w, d)
      })
      return
    case 'dancefloor':
      return // fully live
    case 'mushroom': case 'fern': case 'decal':
      drawFlatClutter(ctx, p)
      return
    case 'crosswalk':
      onFloor(ctx, 0.005, () => {
        ctx.fillStyle = 'rgba(255,255,255,0.85)'
        if (w >= d) for (let u = p.x + 0.1; u < p.x + w - 0.1; u += 0.5) ctx.fillRect(u, p.y + 0.15, 0.28, d - 0.3)
        else for (let u = p.y + 0.1; u < p.y + d - 0.1; u += 0.5) ctx.fillRect(p.x + 0.15, u, w - 0.3, 0.28)
      })
      return
    default:
      return
  }
}

/** Animated layer for flat props, drawn every frame over the floor cache. */
export function drawFlatLive(ctx: Ctx, p: Prop, t: number, day: number): void {
  const { w, d } = footprint(p)
  if (p.kind === 'pond') {
    onFloor(ctx, 0.005, () => {
      const cx = p.x + w / 2, cy = p.y + d / 2
      ctx.lineWidth = 0.035
      for (let i = 0; i < 3; i++) {
        const k = ((t * 0.35 + i / 3) % 1)
        ctx.strokeStyle = `rgba(255,255,255,${0.35 * (1 - k)})`
        ctx.beginPath(); ctx.ellipse(cx - 0.6 + i * 0.7, cy - 0.2 + (i % 2) * 0.5, 0.2 + k * 0.6, 0.15 + k * 0.45, 0, 0, Math.PI * 2); ctx.stroke()
      }
      for (let i = 0; i < 8; i++) {
        const tw = Math.max(0, Math.sin(t * 2 + i * 1.7))
        ctx.fillStyle = `rgba(255,255,255,${0.6 * tw * (0.4 + day * 0.6)})`
        ctx.fillRect(p.x + 0.5 + rnd(i, 3) * (w - 1), p.y + 0.4 + rnd(i, 4) * (d - 0.8), 0.12, 0.04)
      }
    })
  } else if (p.kind === 'water') {
    onFloor(ctx, 0.005, () => {
      // Foam at the shore edge, breathing in and out.
      const surge = 0.5 + 0.5 * Math.sin(t * 0.9)
      ctx.beginPath()
      ctx.moveTo(p.x, p.y + d)
      for (let x = 0; x <= w; x += 0.25) ctx.lineTo(p.x + x, p.y + d - 0.12 - surge * 0.22 - 0.06 * Math.sin(x * 2.1 + t * 1.6))
      ctx.lineTo(p.x + w, p.y + d + 0.08 + surge * 0.1)
      ctx.lineTo(p.x, p.y + d + 0.08 + surge * 0.1)
      ctx.closePath()
      ctx.fillStyle = 'rgba(255,255,255,0.78)'; ctx.fill()
      ctx.strokeStyle = 'rgba(255,255,255,0.35)'; ctx.lineWidth = 0.05
      for (let r = 0; r < 4; r++) {
        ctx.beginPath()
        const y0 = p.y + 0.3 + r * 0.6
        for (let x = 0; x <= w; x += 0.3) {
          const y = y0 + 0.07 * Math.sin(x * 1.7 + t * (1.1 + r * 0.2) + r)
          if (x === 0) ctx.moveTo(p.x + x, y); else ctx.lineTo(p.x + x, y)
        }
        ctx.stroke()
      }
      for (let i = 0; i < 14; i++) {
        const tw = Math.max(0, Math.sin(t * 1.7 + i * 2.3))
        ctx.fillStyle = `rgba(255,255,255,${0.7 * tw * (0.3 + 0.7 * day)})`
        ctx.fillRect(p.x + rnd(i, 7) * w, p.y + 0.2 + rnd(i, 8) * (d - 0.6), 0.18, 0.05)
      }
    })
  } else if (p.kind === 'dancefloor') {
    const cols = ['#ff5d8f', '#4fd1ff', '#ffd166', '#7cf29c', '#b07bff']
    onFloor(ctx, 0.006, () => {
      for (let i = 0; i < w * 2; i++) for (let j = 0; j < d * 2; j++) {
        const k = Math.floor(t * 2.2 + rnd(i, j) * 5)
        const c = cols[(k + i + j) % cols.length]!
        const lit = 0.25 + 0.55 * Math.max(0, Math.sin(t * 3 + i * 0.9 + j * 1.3))
        ctx.fillStyle = alpha(c, lit)
        ctx.fillRect(p.x + i * 0.5 + 0.03, p.y + j * 0.5 + 0.03, 0.44, 0.44)
      }
    })
  }
}

// ─── Standing props ──────────────────────────────────────────────────────

/** Draw a standing prop. `t` seconds; live kinds animate, cached kinds get t = 0. */
export function drawProp(ctx: Ctx, p: Prop, t: number, day: number, seed = variantOf(p)): void {
  const { w, d } = footprint(p)
  const x = p.x, y = p.y
  const cx = x + w / 2, cy = y + d / 2
  switch (p.kind) {
    case 'building':
      drawBuilding(ctx, p, day)
      return
    case 'hatstand': {
      blobShadow(ctx, cx, cy, 0.3, 0.18)
      cyl(ctx, cx, cy, 0, 0.24, 0.05, '#c9a24a')
      cyl(ctx, cx, cy, 0.05, 0.03, 1.1, '#d9c27a')
      const head = iso(cx, cy, 1.3)
      sphere(ctx, head, 9, '#f6efe6')
      drawHatAt(ctx, (p.text ?? 'party') as HatId, head.x, head.y - 6, 0.4, 0, '#f6efe6')
      return
    }
    case 'tree': {
      blobShadow(ctx, cx + 0.15, cy + 0.15, 0.62, 0.24)
      cyl(ctx, cx, cy, 0, 0.13, 1.25, '#9a6b4a')
      const sway = Math.sin(t * 0.9 + seed) * 1.5
      const base = iso(cx, cy, 1.9)
      const blobs: Array<[number, number, number, number]> = [[-15, 4, 19, 4], [15, 2, 18, 1], [0, -10, 22, 0], [-8, -24, 15, 2], [9, -22, 14, 3], [0, 10, 18, 1]]
      for (const [dx, dy, r, c] of blobs) sphere(ctx, { x: base.x + dx + sway * (-dy / 30 + 0.5), y: base.y + dy }, r, LEAF[(c + seed) % LEAF.length]!)
      return
    }
    case 'pine': {
      blobShadow(ctx, cx + 0.12, cy + 0.12, 0.5, 0.24)
      cyl(ctx, cx, cy, 0, 0.11, 0.7, '#8a5c3c')
      const b = iso(cx, cy, 0.6)
      const tiers: Array<[number, number, number]> = [[0, 26, 36], [-22, 21, 32], [-42, 15, 26]]
      tiers.forEach(([oy, hw, h], i) => {
        const g = ctx.createLinearGradient(b.x - hw, 0, b.x + hw, 0)
        g.addColorStop(0, i === 2 ? '#4fb37a' : '#43a06c'); g.addColorStop(1, '#2f7d55')
        ctx.fillStyle = g
        ctx.beginPath(); ctx.moveTo(b.x - hw, b.y + oy); ctx.quadraticCurveTo(b.x, b.y + oy + 7, b.x + hw, b.y + oy); ctx.lineTo(b.x, b.y + oy - h); ctx.closePath(); ctx.fill()
      })
      return
    }
    case 'palm': {
      blobShadow(ctx, cx + 0.5, cy + 0.3, 0.7, 0.2)
      const bend = Math.sin(t * 0.8 + seed) * 2
      let prev = iso(cx, cy, 0)
      for (let i = 1; i <= 8; i++) {
        const z = i * 0.36
        const p2 = iso(cx, cy, z)
        const q = { x: p2.x + Math.pow(i / 8, 2) * (14 + bend), y: p2.y }
        ctx.strokeStyle = i % 2 ? '#b98a5a' : '#a77a4c'; ctx.lineWidth = 8 - i * 0.4; ctx.lineCap = 'round'
        ctx.beginPath(); ctx.moveTo(prev.x, prev.y); ctx.lineTo(q.x, q.y); ctx.stroke()
        prev = q
      }
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2 + 0.3
        const len = 34 + rnd(i, seed) * 8
        const ex = prev.x + Math.cos(a) * len, ey = prev.y + Math.sin(a) * len * 0.45 + 10
        ctx.strokeStyle = i % 2 ? '#4fb97d' : '#6ad89a'; ctx.lineWidth = 7
        ctx.beginPath(); ctx.moveTo(prev.x, prev.y); ctx.quadraticCurveTo((prev.x + ex) / 2 + bend, (prev.y + ey) / 2 - 16, ex, ey); ctx.stroke()
      }
      for (const [dx, dy] of [[-4, 4], [4, 5], [0, 8]] as const) sphere(ctx, { x: prev.x + dx, y: prev.y + dy }, 4, '#8a5c3c')
      return
    }
    case 'bush': {
      blobShadow(ctx, cx + 0.1, cy + 0.1, 0.5, 0.22)
      const b = iso(cx, cy, 0.35)
      for (const [dx, dy, r, c] of [[-10, 2, 13, 1], [10, 3, 12, 4], [0, -6, 14, 0], [0, 6, 11, 2]] as const) sphere(ctx, { x: b.x + dx, y: b.y + dy }, r, LEAF[(c + seed) % LEAF.length]!)
      return
    }
    case 'flowers': {
      castShadow(ctx, x + 0.1, y + 0.1, 0.8, 0.8, 0.3, 0.12)
      box(ctx, x + 0.1, y + 0.1, 0, 0.8, 0.8, 0.3, '#c98d62')
      poly(ctx, [iso(x + 0.18, y + 0.18, 0.3), iso(x + 0.82, y + 0.18, 0.3), iso(x + 0.82, y + 0.82, 0.3), iso(x + 0.18, y + 0.82, 0.3)], '#6b4a34')
      const c = p.color ?? '#ff8fab'
      for (let i = 0; i < 9; i++) {
        const fx = x + 0.25 + (i % 3) * 0.25 + (rnd(i, seed) - 0.5) * 0.08, fy = y + 0.25 + Math.floor(i / 3) * 0.25
        const q = iso(fx, fy, 0.42)
        ctx.fillStyle = '#4fb97d'; ctx.fillRect(q.x - 0.7, q.y, 1.4, 6)
        ctx.fillStyle = i % 4 === 0 ? '#ffffff' : c
        for (let k = 0; k < 5; k++) { const a = (k / 5) * Math.PI * 2; ctx.beginPath(); ctx.arc(q.x + Math.cos(a) * 2.6, q.y + Math.sin(a) * 2.2, 2.2, 0, Math.PI * 2); ctx.fill() }
        ctx.fillStyle = '#ffd166'; ctx.beginPath(); ctx.arc(q.x, q.y, 1.4, 0, Math.PI * 2); ctx.fill()
      }
      return
    }
    case 'hedge':
      castShadow(ctx, x, y, 1, 1, 0.8, 0.14)
      box(ctx, x + 0.05, y + 0.05, 0, 0.9, 0.9, 0.8, '#4fb37a')
      return
    case 'bench': {
      const along = !p.rot // runs along x
      castShadow(ctx, x + 0.1, y + 0.15, w - 0.2, d - 0.3, 0.5, 0.12)
      const lx = along ? [x + 0.15, x + w - 0.3] : [x + 0.25, x + 0.25]
      const ly = along ? [y + 0.3, y + 0.3] : [y + 0.15, y + d - 0.3]
      for (let i = 0; i < 2; i++) box(ctx, lx[i]!, ly[i]!, 0, 0.12, 0.45, 0.38, INK)
      if (along) {
        box(ctx, x + 0.08, y + 0.12, 0.62, w - 0.16, 0.12, 0.48, WOOD) // back, on the far (y-min) edge
        box(ctx, x + 0.08, y + 0.2, 0.38, w - 0.16, 0.6, 0.09, shade(WOOD, 0.05))
      } else {
        box(ctx, x + 0.12, y + 0.08, 0.62, 0.12, d - 0.16, 0.48, WOOD)
        box(ctx, x + 0.2, y + 0.08, 0.38, 0.6, d - 0.16, 0.09, shade(WOOD, 0.05))
      }
      return
    }
    case 'lamppost': {
      blobShadow(ctx, cx, cy, 0.3, 0.2)
      cyl(ctx, cx, cy, 0, 0.16, 0.18, '#3e4257')
      cyl(ctx, cx, cy, 0.18, 0.06, 2.35, '#4a4f68')
      const lit = 1 - day
      const head = iso(cx, cy, 2.75)
      ctx.fillStyle = '#3e4257'
      ctx.beginPath(); ctx.moveTo(head.x - 9, head.y - 10); ctx.lineTo(head.x + 9, head.y - 10); ctx.lineTo(head.x + 5, head.y - 16); ctx.lineTo(head.x - 5, head.y - 16); ctx.closePath(); ctx.fill()
      ctx.fillStyle = mix('#f6efe0', '#ffe39a', lit)
      ctx.beginPath(); ctx.moveTo(head.x - 7, head.y - 10); ctx.lineTo(head.x + 7, head.y - 10); ctx.lineTo(head.x + 5, head.y + 6); ctx.lineTo(head.x - 5, head.y + 6); ctx.closePath(); ctx.fill()
      ctx.fillStyle = '#3e4257'; ctx.fillRect(head.x - 6, head.y + 6, 12, 3)
      return
    }
    case 'fountain': {
      const r = 1.38
      blobShadow(ctx, cx + 0.2, cy + 0.2, 1.7, 0.2)
      cyl(ctx, cx, cy, 0, r, 0.42, '#cfc6b8', { top: '#e2dbd0' })
      const top = iso(cx, cy, 0.42)
      const rx = (r - 0.16) * TW / 2, ry = (r - 0.16) * TH / 2
      const g = ctx.createRadialGradient(top.x, top.y, 0, top.x, top.y, rx)
      g.addColorStop(0, '#8fd6ef'); g.addColorStop(1, '#4f9cc4')
      ctx.fillStyle = g; ctx.beginPath(); ctx.ellipse(top.x, top.y, rx, ry, 0, 0, Math.PI * 2); ctx.fill()
      ctx.strokeStyle = 'rgba(255,255,255,0.5)'; ctx.lineWidth = 1.2
      for (let i = 0; i < 3; i++) {
        const k = (t * 0.5 + i / 3) % 1
        ctx.globalAlpha = 1 - k
        ctx.beginPath(); ctx.ellipse(top.x, top.y, rx * (0.25 + k * 0.7), ry * (0.25 + k * 0.7), 0, 0, Math.PI * 2); ctx.stroke()
      }
      ctx.globalAlpha = 1
      cyl(ctx, cx, cy, 0.42, 0.2, 0.75, '#d8d0c4')
      cyl(ctx, cx, cy, 1.15, 0.55, 0.14, '#cfc6b8', { top: '#8fd6ef' })
      cyl(ctx, cx, cy, 1.29, 0.1, 0.35, '#d8d0c4')
      // Jets.
      const spout = iso(cx, cy, 1.66)
      ctx.strokeStyle = 'rgba(200,236,255,0.85)'; ctx.lineWidth = 2
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2
        const ex = spout.x + Math.cos(a) * 30, ey = spout.y + 30 + Math.sin(a) * 14
        ctx.beginPath(); ctx.moveTo(spout.x, spout.y); ctx.quadraticCurveTo(spout.x + Math.cos(a) * 16, spout.y - 18 - Math.sin(t * 4 + i) * 2, ex, ey); ctx.stroke()
      }
      for (let i = 0; i < 10; i++) {
        const k = (t * 1.3 + i / 10) % 1
        const a = (i / 10) * Math.PI * 2
        ctx.fillStyle = `rgba(230,248,255,${0.9 * (1 - k)})`
        ctx.beginPath(); ctx.arc(spout.x + Math.cos(a) * 30 * k, spout.y - 16 * Math.sin(k * Math.PI) + 30 * k * k + Math.sin(a) * 14 * k, 1.6, 0, Math.PI * 2); ctx.fill()
      }
      return
    }
    case 'signpost': {
      blobShadow(ctx, cx, cy, 0.3, 0.18)
      box(ctx, cx - 0.05, cy - 0.05, 0, 0.1, 0.1, 1.5, WOOD)
      const a = iso(cx - 0.05, cy + 0.06, 1.05), b = iso(cx + 0.55, cy + 0.06, 1.05), c = iso(cx + 0.55, cy + 0.06, 1.55), e = iso(cx - 0.05, cy + 0.06, 1.55)
      poly(ctx, [a, b, { x: b.x + 6, y: (b.y + c.y) / 2 }, c, e], '#f3e2c6')
      if (p.text) {
        ctx.save()
        const m = { x: (a.x + c.x) / 2, y: (a.y + c.y) / 2 }
        ctx.translate(m.x, m.y); ctx.transform(1, 0.5, 0, 1, 0, 0)
        ctx.font = '800 7px Nunito, ui-rounded, system-ui, sans-serif'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle'; ctx.fillStyle = '#7a4a2a'
        ctx.fillText(p.text, 1, -4)
        ctx.restore()
      }
      return
    }
    case 'parasol': {
      onFloor(ctx, 0.01, () => { ctx.beginPath(); ctx.ellipse(cx + 0.35, cy + 0.35, 1.1, 1.0, 0, 0, Math.PI * 2); ctx.fillStyle = 'rgba(120,80,40,0.16)'; ctx.fill() })
      cyl(ctx, cx, cy, 0, 0.04, 2.1, '#f2efe8')
      const c = p.color ?? '#ff6b8b'
      const top = iso(cx, cy, 2.45), rim = iso(cx, cy, 1.95)
      const n = 10
      for (let i = 0; i < n; i++) {
        const a0 = (i / n) * Math.PI * 2, a1 = ((i + 1) / n) * Math.PI * 2
        const p0 = { x: rim.x + Math.cos(a0) * 1.15 * TW / 2, y: rim.y + Math.sin(a0) * 1.15 * TH / 2 }
        const p1 = { x: rim.x + Math.cos(a1) * 1.15 * TW / 2, y: rim.y + Math.sin(a1) * 1.15 * TH / 2 }
        const lit = Math.sin((a0 + a1) / 2 - 0.6) * 0.1
        poly(ctx, [top, p0, p1], shade(i % 2 ? c : '#ffffff', lit))
      }
      return
    }
    case 'sandcastle': {
      blobShadow(ctx, cx, cy, 0.5, 0.2)
      const s = '#e6c784'
      box(ctx, x + 0.12, y + 0.12, 0, 0.76, 0.76, 0.35, s)
      cyl(ctx, x + 0.28, y + 0.28, 0.35, 0.14, 0.35, s)
      cyl(ctx, x + 0.72, y + 0.72, 0.35, 0.14, 0.3, s)
      cyl(ctx, cx, cy, 0.35, 0.18, 0.55, s)
      const tip = iso(cx, cy, 0.9)
      ctx.strokeStyle = INK; ctx.lineWidth = 1; ctx.beginPath(); ctx.moveTo(tip.x, tip.y); ctx.lineTo(tip.x, tip.y - 14); ctx.stroke()
      poly(ctx, [{ x: tip.x, y: tip.y - 14 }, { x: tip.x + 9, y: tip.y - 11 }, { x: tip.x, y: tip.y - 8 }], '#ff6b8b')
      return
    }
    case 'campfire': {
      for (let i = 0; i < 8; i++) {
        const a = (i / 8) * Math.PI * 2
        const q = iso(cx + Math.cos(a) * 0.36, cy + Math.sin(a) * 0.36, 0.06)
        ctx.fillStyle = i % 2 ? '#9a948c' : '#b4ada3'; ctx.beginPath(); ctx.ellipse(q.x, q.y, 5, 3.5, 0, 0, Math.PI * 2); ctx.fill()
      }
      box(ctx, cx - 0.3, cy - 0.06, 0.02, 0.6, 0.12, 0.1, '#7a4a2a')
      box(ctx, cx - 0.06, cy - 0.3, 0.06, 0.12, 0.6, 0.1, '#8a5a36')
      const b = iso(cx, cy, 0.12)
      for (let i = 0; i < 3; i++) {
        const k = Math.sin(t * (7 + i) + i * 2)
        const h = 20 + i * -5 + k * 4
        const wdt = 9 - i * 2.5
        ctx.fillStyle = ['#ff8a3c', '#ffc23c', '#fff1a8'][i]!
        ctx.beginPath(); ctx.moveTo(b.x - wdt, b.y); ctx.quadraticCurveTo(b.x - wdt * 0.6 + k, b.y - h * 0.6, b.x + k * 1.5, b.y - h); ctx.quadraticCurveTo(b.x + wdt * 0.6 + k, b.y - h * 0.6, b.x + wdt, b.y); ctx.closePath(); ctx.fill()
      }
      for (let i = 0; i < 5; i++) {
        const k = (t * 0.6 + i / 5) % 1
        ctx.fillStyle = `rgba(255,200,90,${1 - k})`
        ctx.fillRect(b.x + Math.sin(i * 3 + t * 2) * 6 * k, b.y - 18 - k * 34, 1.6, 1.6)
      }
      return
    }
    case 'stump':
      blobShadow(ctx, cx, cy, 0.4, 0.2)
      cyl(ctx, cx, cy, 0, 0.3, 0.36, '#9a6b4a', { top: '#e2c08f' })
      ctx.strokeStyle = 'rgba(120,80,40,0.35)'; ctx.lineWidth = 1
      for (const r of [0.1, 0.2]) { const q = iso(cx, cy, 0.36); ctx.beginPath(); ctx.ellipse(q.x, q.y, r * TW / 2, r * TH / 2, 0, 0, Math.PI * 2); ctx.stroke() }
      return
    case 'table': {
      blobShadow(ctx, cx, cy, 0.45, 0.2)
      cyl(ctx, cx, cy, 0, 0.22, 0.04, INK)
      cyl(ctx, cx, cy, 0.04, 0.04, 0.66, '#4a4466')
      const top = p.color ?? '#f3ece2'
      cyl(ctx, cx, cy, 0.7, 0.42, 0.06, top, { top: shade(top, 0.06) })
      cyl(ctx, cx - 0.08, cy + 0.05, 0.76, 0.07, 0.12, '#ffffff', { top: '#7a4a2a' })
      cyl(ctx, cx + 0.14, cy - 0.1, 0.76, 0.12, 0.02, '#ffffff')
      cyl(ctx, cx + 0.14, cy - 0.1, 0.78, 0.07, 0.07, '#f2b56b', { top: '#ffd99a' })
      return
    }
    case 'chair': {
      const c = p.color ?? '#e07a5f'
      castShadow(ctx, x + 0.2, y + 0.2, 0.6, 0.6, 0.5, 0.1)
      for (const [lx, ly] of [[0.24, 0.24], [0.68, 0.24], [0.24, 0.68], [0.68, 0.68]] as const) box(ctx, x + lx, y + ly, 0, 0.07, 0.07, 0.4, INK, { edge: false })
      box(ctx, x + 0.18, y + 0.18, 0.4, 0.64, 0.64, 0.08, c)
      box(ctx, x + 0.18, y + 0.14, 0.48, 0.64, 0.08, 0.52, shade(c, -0.05)) // back on the far edge
      return
    }
    case 'sofa': {
      const c = p.color ?? '#7d8fd6'
      castShadow(ctx, x, y, w, d, 0.8, 0.16)
      if (!p.rot) {
        box(ctx, x + 0.05, y + 0.1, 0, w - 0.1, 0.8, 0.38, shade(c, -0.08))
        box(ctx, x + 0.05, y + 0.05, 0.38, w - 0.1, 0.24, 0.48, c)
        box(ctx, x + 0.05, y + 0.29, 0.38, 0.18, 0.61, 0.22, shade(c, 0.04))
        box(ctx, x + w - 0.23, y + 0.29, 0.38, 0.18, 0.61, 0.22, shade(c, 0.04))
        for (let i = 0; i < w; i++) box(ctx, x + 0.25 + i * (w - 0.5) / w, y + 0.31, 0.38, (w - 0.5) / w - 0.04, 0.55, 0.1, shade(c, 0.1))
      } else {
        box(ctx, x + 0.1, y + 0.05, 0, 0.8, d - 0.1, 0.38, shade(c, -0.08))
        box(ctx, x + 0.05, y + 0.05, 0.38, 0.24, d - 0.1, 0.48, c)
        box(ctx, x + 0.29, y + 0.05, 0.38, 0.61, 0.18, 0.22, shade(c, 0.04))
        box(ctx, x + 0.29, y + d - 0.23, 0.38, 0.61, 0.18, 0.22, shade(c, 0.04))
        for (let i = 0; i < d; i++) box(ctx, x + 0.31, y + 0.25 + i * (d - 0.5) / d, 0.38, 0.55, (d - 0.5) / d - 0.04, 0.1, shade(c, 0.1))
      }
      return
    }
    case 'counter': {
      castShadow(ctx, x, y, w, d, 1, 0.14)
      box(ctx, x + 0.04, y + 0.08, 0, w - 0.08, d - 0.12, 0.96, '#c98d62')
      // Wainscot panels on the customer side.
      for (let i = 0; i < w; i++) poly(ctx, faceY(y + d - 0.04, x + i + 0.15, x + i + 0.85, 0.15, 0.8), 'rgba(255,255,255,0.12)')
      box(ctx, x, y + 0.04, 0.96, w, d - 0.04, 0.08, '#f4efe8')
      // Espresso machine, cups and a cake dome.
      box(ctx, x + 0.35, y + 0.18, 1.04, 0.7, 0.5, 0.5, '#d9dde6')
      poly(ctx, faceY(y + 0.68, x + 0.45, x + 0.95, 1.2, 1.44), '#3a3552')
      cyl(ctx, x + 0.65, y + 0.75, 1.04, 0.06, 0.1, '#ffffff')
      cyl(ctx, x + w - 1.2, y + 0.5, 1.04, 0.28, 0.04, '#f4f1ec')
      const dome = iso(x + w - 1.2, y + 0.5, 1.1)
      sphere(ctx, { x: dome.x, y: dome.y - 4 }, 9, '#f2b56b')
      ctx.fillStyle = 'rgba(220,240,255,0.35)'; ctx.beginPath(); ctx.ellipse(dome.x, dome.y - 4, 13, 14, 0, Math.PI, 0); ctx.fill()
      for (let i = 0; i < 3; i++) cyl(ctx, x + 1.8 + i * 0.32, y + 0.55, 1.04, 0.08, 0.12, ['#ff8fab', '#7ee0c3', '#ffd166'][i]!)
      return
    }
    case 'shelf': {
      castShadow(ctx, x, y, w, d, 2, 0.12)
      box(ctx, x, y, 0, w, d, 2.1, '#a8744c')
      const front = p.rot ? 'x' : 'y'
      const len = p.rot ? d : w
      const cols = ['#e07a5f', '#3d405b', '#81b29a', '#f2b56b', '#8d7fd6', '#5aa9c9', '#ff8fab']
      for (let s = 0; s < 4; s++) {
        const z0 = 0.12 + s * 0.5
        let a = 0.08
        let i = 0
        while (a < len - 0.12) {
          const bw = 0.08 + rnd(s, i, seed) * 0.08
          const bh = 0.3 + rnd(i, s, 7) * 0.12
          const c = cols[(i + s * 3 + seed) % cols.length]!
          if (front === 'y') poly(ctx, faceY(y + d, x + a, x + a + bw, z0, z0 + bh), c)
          else poly(ctx, faceX(x + w, y + a, y + a + bw, z0, z0 + bh), shade(c, -0.12))
          a += bw + 0.015
          i++
        }
        if (front === 'y') poly(ctx, faceY(y + d, x, x + w, z0 - 0.06, z0), '#8a5c3c')
        else poly(ctx, faceX(x + w, y, y + d, z0 - 0.06, z0), '#7a4f33')
      }
      return
    }
    case 'plant': {
      const potC = ['#d98c6b', '#8ea8b8', '#c9b7a0'][seed % 3]!
      blobShadow(ctx, cx, cy, 0.34, 0.2)
      cyl(ctx, cx, cy, 0, 0.2, 0.08, shade(potC, -0.08))
      cyl(ctx, cx, cy, 0.08, 0.24, 0.32, potC, { top: '#5a3f2c' })
      const base = iso(cx, cy, 0.4)
      const leaves: Array<[number, number, number, number, string]> = [[-11, -9, 9, -0.5, '#4fb97d'], [11, -11, 9, 0.5, '#6ad89a'], [0, -19, 11, 0, '#5cc98a'], [-6, -27, 8, -0.3, '#7ee0a8'], [7, -25, 8, 0.3, '#4fb97d'], [0, -33, 6, 0, '#8be8b4']]
      for (const [dx, dy, r, rot, c] of leaves) {
        ctx.fillStyle = c
        ctx.beginPath(); ctx.ellipse(base.x + dx, base.y + dy, r, r * 0.78, rot, 0, Math.PI * 2); ctx.fill()
      }
      return
    }
    case 'lamp': {
      blobShadow(ctx, cx, cy, 0.3, 0.18)
      cyl(ctx, cx, cy, 0, 0.2, 0.05, INK)
      cyl(ctx, cx, cy, 0.05, 0.035, 1.5, '#4a4466')
      const lit = 1 - day
      const a = iso(cx, cy, 1.55), b = iso(cx, cy, 1.95)
      ctx.fillStyle = mix('#f6e7cf', '#ffe2a0', lit)
      ctx.beginPath(); ctx.moveTo(a.x - 13, a.y); ctx.lineTo(a.x + 13, a.y); ctx.lineTo(b.x + 8, b.y); ctx.lineTo(b.x - 8, b.y); ctx.closePath(); ctx.fill()
      ctx.fillStyle = 'rgba(0,0,0,0.08)'
      ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(a.x + 13, a.y); ctx.lineTo(b.x + 8, b.y); ctx.lineTo(b.x, b.y); ctx.closePath(); ctx.fill()
      return
    }
    case 'arcade': {
      const c = p.color ?? '#ff5d8f'
      castShadow(ctx, x + 0.1, y, 0.8, 0.9, 1.9, 0.14)
      box(ctx, x + 0.1, y + 0.05, 0, 0.8, 0.85, 1.85, '#2d2850', { left: '#3a3366', right: '#25213f' })
      // Side art stripe.
      poly(ctx, faceX(x + 0.9, y + 0.15, y + 0.8, 0.3, 1.6), alpha(c, 0.55))
      // Screen and marquee on the front (+y) face.
      const flick = 0.85 + 0.15 * Math.sin(t * 13 + seed)
      poly(ctx, faceY(y + 0.9, x + 0.2, x + 0.8, 1.05, 1.55), mix('#111', c, 0.55 * flick))
      for (let i = 0; i < 3; i++) {
        const px = x + 0.25 + ((t * 0.3 + i * 0.33 + rnd(i, seed)) % 1) * 0.5
        poly(ctx, faceY(y + 0.9, px, px + 0.06, 1.15 + i * 0.12, 1.2 + i * 0.12), '#ffffff')
      }
      poly(ctx, faceY(y + 0.9, x + 0.15, x + 0.85, 1.65, 1.82), c)
      box(ctx, x + 0.12, y + 0.85, 0.9, 0.76, 0.22, 0.08, '#3a3366')
      const j = iso(x + 0.35, y + 0.98, 0.98)
      ctx.fillStyle = '#ff5d5d'; ctx.beginPath(); ctx.arc(j.x, j.y - 4, 2.6, 0, Math.PI * 2); ctx.fill()
      return
    }
    case 'beanbag': {
      const c = p.color ?? '#ff8fab'
      blobShadow(ctx, cx, cy, 0.48, 0.24)
      const b = iso(cx, cy, 0.2)
      ctx.fillStyle = shade(c, -0.1); ctx.beginPath(); ctx.ellipse(b.x, b.y + 2, 24, 13, 0, 0, Math.PI * 2); ctx.fill()
      sphere(ctx, { x: b.x - 2, y: b.y - 5 }, 15, c)
      ctx.fillStyle = shade(c, 0.12); ctx.beginPath(); ctx.ellipse(b.x + 3, b.y, 16, 8, 0, 0, Math.PI * 2); ctx.fill()
      return
    }
    case 'djbooth': {
      castShadow(ctx, x, y, w, d, 1.1, 0.14)
      box(ctx, x + 0.05, y + 0.1, 0, w - 0.1, d - 0.15, 1.0, '#2d2850', { left: '#3a3366', right: '#25213f' })
      const cols = ['#ff5d8f', '#4fd1ff', '#7cf29c', '#ffd166']
      for (let i = 0; i < 8; i++) {
        const h = 0.15 + 0.6 * Math.abs(Math.sin(t * (3 + i * 0.4) + i))
        poly(ctx, faceY(y + d - 0.05, x + 0.2 + i * 0.2, x + 0.33 + i * 0.2, 0.15, 0.15 + h), cols[i % 4]!)
      }
      for (const [tx] of [[0.55], [1.45]] as const) {
        const q = iso(x + tx, y + 0.5, 1.0)
        ctx.fillStyle = '#1b1830'; ctx.beginPath(); ctx.ellipse(q.x, q.y, 13, 6.5, 0, 0, Math.PI * 2); ctx.fill()
        ctx.strokeStyle = 'rgba(255,255,255,0.2)'; ctx.lineWidth = 1
        ctx.beginPath(); ctx.ellipse(q.x, q.y, 8, 4, t * 4, 0, Math.PI); ctx.stroke()
        ctx.fillStyle = '#ff5d8f'; ctx.beginPath(); ctx.ellipse(q.x, q.y, 2.5, 1.3, 0, 0, Math.PI * 2); ctx.fill()
      }
      return
    }
    case 'vending': {
      const c = p.color ?? '#ff5d8f'
      castShadow(ctx, x, y, 0.9, 0.9, 2, 0.14)
      box(ctx, x + 0.05, y + 0.08, 0, 0.8, 0.84, 2.0, c)
      // Window on the +x face (it stands against the left wall).
      poly(ctx, faceX(x + 0.85, y + 0.16, y + 0.66, 0.75, 1.85), '#1f2a44')
      const items = ['#ffd166', '#7cf29c', '#4fd1ff', '#ff8fab', '#ffffff']
      for (let r = 0; r < 4; r++) for (let k = 0; k < 3; k++) poly(ctx, faceX(x + 0.85, y + 0.2 + k * 0.15, y + 0.3 + k * 0.15, 0.85 + r * 0.25, 1.0 + r * 0.25), items[(r + k) % items.length]!)
      poly(ctx, faceX(x + 0.85, y + 0.72, y + 0.84, 1.1, 1.5), '#2d2a3e')
      poly(ctx, faceX(x + 0.85, y + 0.2, y + 0.62, 0.2, 0.45), '#2d2a3e')
      return
    }
    case 'piano': {
      castShadow(ctx, x, y, w, d, 1.3, 0.14)
      box(ctx, x + 0.05, y + 0.05, 0, w - 0.1, 0.55, 1.3, '#4a2f2a')
      box(ctx, x + 0.08, y + 0.55, 0.62, w - 0.16, 0.3, 0.08, '#4a2f2a')
      // Keys.
      poly(ctx, [iso(x + 0.12, y + 0.6, 0.7), iso(x + w - 0.12, y + 0.6, 0.7), iso(x + w - 0.12, y + 0.82, 0.7), iso(x + 0.12, y + 0.82, 0.7)], '#fbf7f1')
      for (let i = 0; i < 18; i++) if (i % 7 !== 2 && i % 7 !== 6) {
        const kx = x + 0.15 + i * ((w - 0.3) / 18)
        poly(ctx, [iso(kx, y + 0.6, 0.71), iso(kx + 0.05, y + 0.6, 0.71), iso(kx + 0.05, y + 0.7, 0.71), iso(kx, y + 0.7, 0.71)], '#2d2a3e')
      }
      cyl(ctx, x + 0.4, y + 0.3, 1.3, 0.08, 0.2, '#fbf7f1', { top: '#ffd166' })
      return
    }
    default:
      if (!drawExtra(ctx, p, t, day, seed)) drawClutter(ctx, p, t, day, seed)
  }
}

/** Light sources a prop contributes at night. */
export function propLights(p: Prop): Light[] {
  const { w, d } = footprint(p)
  const cx = p.x + w / 2, cy = p.y + d / 2
  switch (p.kind) {
    case 'building': {
      if (p.style === 'lighthouse') return [{ x: cx, y: cy, z: wallHeight(p) + 0.5, r: 160, color: '#fff3b0', a: 0.6 }]
      if (p.style === 'tower') return [{ x: p.x + w + 0.5, y: cy, z: 0.6, r: 140, color: '#ffe3a0', a: 0.35 }, { x: cx, y: p.y + d + 0.5, z: 0.6, r: 140, color: '#ffe3a0', a: 0.35 }]
      // Warm light spilling from the door and the front windows.
      const dr = doorOf(p)
      const out: Light[] = [{ x: dr.x + (p.rot ? 0.6 : 0.5), y: dr.y + (p.rot ? 0.5 : 0.6), z: 0.9, r: 110, color: '#ffcf8a', a: 0.4 }]
      for (const wx of windowsOf(p)) out.push(p.rot ? { x: p.x + w + 0.5, y: wx + 0.4, z: 1.4, r: 60, color: '#ffd98a', a: 0.3 } : { x: wx + 0.4, y: p.y + d + 0.5, z: 1.4, r: 60, color: '#ffd98a', a: 0.3 })
      return out
    }
    case 'hatstand': return []
    case 'festoon': return p.variant === 'lanterns' ? [{ x: cx, y: cy, z: 2.2, r: 90, color: '#ff6b4a', a: 0.35 }] : [{ x: cx, y: cy, z: 2.3, r: 100, color: '#ffd98a', a: 0.4, flicker: 0.15 }]
    case 'cart': return [{ x: cx, y: cy + 0.4, z: 1.2, r: 70, color: '#ffcf8a', a: 0.35 }]
    case 'stall': return [{ x: cx, y: cy + 0.4, z: 1.4, r: 60, color: '#ffcf8a', a: 0.3 }]
    case 'lanternpost': return [{ x: cx + 0.3, y: cy, z: 1.6, r: 70, color: '#ff6b4a', a: 0.45, flicker: 0.2 }]
    case 'stonelantern': return [{ x: cx, y: cy, z: 0.8, r: 50, color: '#ffcf8a', a: 0.4 }]
    case 'streetlamp': return [{ x: cx + 0.4, y: cy - 0.4, z: 2.8, r: 120, color: '#e8f0ff', a: 0.45 }]
    case 'tramstop': return [{ x: cx, y: cy + 0.3, z: 1.8, r: 60, color: '#7cf2d0', a: 0.35 }]
    case 'gate': return p.variant === 'neon' ? [{ x: cx, y: cy, z: 2.6, r: 120, color: '#ff5dd8', a: 0.5, flicker: 0.4 }] : p.variant === 'paifang' ? [{ x: cx, y: cy, z: 1.7, r: 80, color: '#ff6b4a', a: 0.35 }] : []
    case 'lamppost': return [{ x: cx, y: cy, z: 2.7, r: 120, color: '#ffd28a', a: 0.5 }]
    case 'lamp': return [{ x: cx, y: cy, z: 1.8, r: 100, color: '#ffcf8a', a: 0.45 }]
    case 'campfire': return [{ x: cx, y: cy, z: 0.4, r: 150, color: '#ff9a4a', a: 0.6, flicker: 1 }]
    case 'arcade': return [{ x: cx, y: cy + 0.6, z: 1.3, r: 70, color: p.color ?? '#ff5d8f', a: 0.45, flicker: 0.3 }]
    case 'djbooth': return [{ x: cx, y: cy + 0.4, z: 1, r: 110, color: '#b07bff', a: 0.45, flicker: 0.5 }]
    case 'vending': return [{ x: cx + 0.5, y: cy, z: 1.3, r: 70, color: p.color ?? '#4fd1ff', a: 0.35 }]
    case 'fountain': return [{ x: cx, y: cy, z: 0.5, r: 90, color: '#8fd6ef', a: 0.25 }]
    case 'dancefloor': return [{ x: cx, y: cy, z: 0, r: 150, color: '#ff5dd8', a: 0.35, flicker: 0.6 }]
    default: return []
  }
}

/** Screen bounds (world px, before camera) of a standing prop, generous. */
export function propBounds(p: Prop): { x: number; y: number; w: number; h: number } {
  const { w, d } = footprint(p)
  const h = p.kind === 'building' ? buildingTop(p) : HEIGHT[p.kind] ?? 1.2
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  for (const xx of [p.x - 0.6, p.x + w + 0.6]) for (const yy of [p.y - 0.6, p.y + d + 0.6]) for (const z of [0, h + 0.4]) {
    const q = iso(xx, yy, z)
    minX = Math.min(minX, q.x); maxX = Math.max(maxX, q.x); minY = Math.min(minY, q.y); maxY = Math.max(maxY, q.y)
  }
  const pad = p.kind === 'windmill' ? 70 : p.kind === 'palm' || p.kind === 'willow' ? 46 : p.kind === 'tree' || p.kind === 'sakura' || p.kind === 'building' ? 30 : 16
  return { x: Math.floor(minX - pad), y: Math.floor(minY - pad), w: Math.ceil(maxX - minX + pad * 2), h: Math.ceil(maxY - minY + pad * 2) }
}
